/**
 * Proposal Project API — Connect RPC implementation
 * Calls ProjectDataService via sabaody gateway.
 * Maps proto response shapes to frontend types defined in types/proposal.ts.
 */ import { ProjectDataTypes } from '@mono/proto-clients';
import { ComplianceStatus, ObligationStrength, RequirementCategory } from '../../types/proposal';
import { projectDataClient } from '../connect/clients';
// ---------------------------------------------------------------------------
// Proto → Frontend mappers
// ---------------------------------------------------------------------------
function toEnum(value, enumObj, fallback) {
    for (const v of Object.values(enumObj)){
        if (v === value) return v;
    }
    return fallback;
}
const DEFAULT_SOURCE = {
    documentId: '',
    documentType: '',
    filename: '',
    pageNumber: 0,
    exactText: ''
};
function mapRequirement(r) {
    return {
        requirementId: r.requirementId,
        sequenceNumber: r.sequenceNumber,
        source: r.source ? {
            documentId: r.source.documentId,
            documentType: r.source.documentType,
            filename: r.source.filename,
            sectionNumber: r.source.sectionNumber || undefined,
            pageNumber: r.source.pageNumber,
            blockId: r.source.blockId || undefined,
            exactText: r.source.exactText
        } : DEFAULT_SOURCE,
        category: toEnum(r.category, RequirementCategory, RequirementCategory.OTHER),
        categories: r.categories,
        obligationStrength: toEnum(r.obligationStrength, ObligationStrength, ObligationStrength.SHALL),
        isMandatory: r.isMandatory,
        isEvaluated: r.isEvaluated,
        evaluationFactor: r.evaluationFactor || undefined,
        evaluationSubfactor: r.evaluationSubfactor || undefined,
        evaluationWeight: r.evaluationWeight || undefined,
        entities: r.entities,
        relatedCLINs: r.relatedClins,
        sectionContext: r.sectionContext ? {
            fullPath: r.sectionContext.fullPath || undefined,
            topLevel: r.sectionContext.topLevel || undefined,
            sectionNumber: r.sectionContext.sectionNumber || undefined,
            title: r.sectionContext.title || undefined,
            depth: r.sectionContext.depth || undefined,
            isPWS: r.sectionContext.isPws || undefined,
            isAddendum: r.sectionContext.isAddendum || undefined
        } : undefined,
        responseAction: r.responseAction || undefined,
        deliverableType: r.deliverableType || undefined,
        actionOwner: r.actionOwner || undefined,
        status: toEnum(r.status, ComplianceStatus, ComplianceStatus.NOT_STARTED),
        statusHistory: [],
        notes: r.notes || undefined,
        tags: r.tags,
        confidence: r.confidence,
        extractedAt: r.extractedAt,
        extractedBy: r.extractedBy,
        assignedToUserId: r.assignedToUserId || undefined,
        mapping: r.mapping ? {
            proposalSection: r.mapping.proposalSection,
            responseText: r.mapping.responseText || undefined,
            pageReference: r.mapping.pageReference || undefined
        } : undefined,
        sourceDocumentId: r.sourceDocumentId || undefined,
        sourceFilename: r.sourceFilename || undefined,
        amendmentSequence: r.amendmentSequence || undefined,
        logicalKey: r.logicalKey || undefined,
        isCurrentVersion: r.isCurrentVersion || undefined,
        // PR-B.7.4 (Issue 5): the proto carries amendment provenance two ways
        // — top-level fields (sourceDocumentId / amendmentSequence / etc) AND
        // a nested AmendmentInfo struct that ONLY gets populated when
        // is_from_amendment=true on the DB row. Without this mapping the
        // RequirementRow badge falls back to displaying the raw amendmentSequence
        // ("AM006" because reverie sequences attachment files into the chain),
        // which is meaningless to users.
        amendmentInfo: r.amendmentInfo ? {
            isFromAmendment: r.amendmentInfo.isFromAmendment,
            amendmentNumber: r.amendmentInfo.amendmentNumber || undefined,
            changeType: r.amendmentInfo.changeType || undefined,
            previousVersion: r.amendmentInfo.previousVersion || undefined,
            supersededById: r.amendmentInfo.supersededById || undefined,
            supersedes: r.amendmentInfo.supersedes || undefined
        } : undefined,
        displayCategory: r.isMandatory ? 'mandatory' : 'optional',
        displayStatus: undefined,
        displaySource: r.source?.filename || undefined
    };
}
function mapEvaluationFactor(f) {
    return {
        name: f.name,
        weight: f.weight || undefined,
        weightType: f.weightType || undefined,
        importanceRank: f.importanceRank || undefined,
        subfactors: f.subfactors?.length ? f.subfactors.map(mapEvaluationFactor) : undefined,
        description: f.description || undefined,
        ratings: f.ratings?.length ? f.ratings : undefined,
        requirementIds: f.requirementIds?.length ? f.requirementIds : undefined
    };
}
function mapEvaluationScheme(s) {
    return {
        factors: (s.factors ?? []).map(mapEvaluationFactor),
        method: s.method,
        methodDetails: s.methodDetails || undefined,
        priceRelation: s.priceRelation || undefined,
        discriminators: s.discriminators?.length ? s.discriminators : undefined,
        sourceSections: s.sourceSections?.length ? s.sourceSections : undefined,
        processingTimeMs: Number(s.processingTimeMs) || undefined
    };
}
function mapStoredDeliverable(d) {
    return {
        deliverableId: d.deliverableId || undefined,
        title: d.title || undefined,
        description: d.description || undefined,
        dueDate: d.dueDate || d.currentDueDate || undefined,
        cadence: d.cadence || undefined,
        deliverableType: d.type || undefined,
        cdrlReference: d.cdrlNumber || undefined
    };
}
// mapReferenceContent translates the proto CLINReferenceContent (returned by
// zou's QueryCLINs) into the IReferenceContent shape the drawer consumes for
// PWS click-to-highlight. Returns an empty object when the proto value is
// missing so callers can spread it unconditionally.
function mapReferenceContent(rc) {
    if (!rc) return {};
    const pwsSections = {};
    for (const [key, value] of Object.entries(rc.pwsSections ?? {})){
        pwsSections[key] = {
            ...value.title ? {
                title: value.title
            } : {},
            text: value.text ?? '',
            ...value.documentId ? {
                documentId: value.documentId
            } : {},
            ...value.filename ? {
                filename: value.filename
            } : {},
            ...value.pageNumber > 0 ? {
                pageNumber: value.pageNumber
            } : {},
            ...value.blockId ? {
                blockId: value.blockId
            } : {},
            ...value.blockIds && value.blockIds.length > 0 ? {
                blockIds: value.blockIds
            } : {}
        };
    }
    const attachments = {};
    for (const [key, value] of Object.entries(rc.attachments ?? {})){
        attachments[key] = {
            filename: value.filename ?? '',
            documentType: value.documentType || 'ATTACHMENT',
            ...value.summary ? {
                summary: value.summary
            } : {}
        };
    }
    if (Object.keys(pwsSections).length === 0 && Object.keys(attachments).length === 0) {
        return {};
    }
    return {
        referenceContent: {
            pwsSections,
            attachments
        }
    };
}
function mapCLIN(c, deliverablesByClinNumber) {
    const pricing = c.pricing;
    const deliverables = deliverablesByClinNumber?.get(c.clinNumber);
    return {
        clinId: c.clinId || undefined,
        clinNumber: c.clinNumber,
        title: c.shortTitle || c.description || c.clinNumber,
        parentClin: c.parentClin || undefined,
        description: c.description || undefined,
        optionType: c.optionType || undefined,
        contractType: c.contractType || undefined,
        pwsReferences: c.pwsSections,
        attachmentReferences: c.attachmentReferences,
        correspondingBaseClin: c.correspondingBaseClin || undefined,
        isOptionalTask: c.isOptionalTask || undefined,
        optionalTaskNumber: c.optionalTaskNumber || undefined,
        optionalTaskTitle: c.optionalTaskTitle || undefined,
        costType: c.costType || undefined,
        needsTeamPricing: c.needsTeamPricing || undefined,
        periodType: c.periodType || undefined,
        periodName: c.periodOfPerformance?.periodName || undefined,
        status: c.fundingStatus || c.status || undefined,
        notes: c.notes || undefined,
        confidence: c.confidence || undefined,
        extractedAt: c.extractedAt || undefined,
        source: c.source ? {
            documentId: c.source.documentId || undefined,
            documentType: c.source.documentType || undefined,
            filename: c.source.filename || undefined,
            blockId: c.source.blockId || undefined,
            sourceBlockIds: c.source.sourceBlockIds?.length ? c.source.sourceBlockIds : undefined,
            sectionNumber: c.source.sectionNumber || undefined,
            pageNumber: c.source.pageNumber ?? undefined
        } : undefined,
        deliverables: deliverables?.length ? deliverables : undefined,
        pricing: pricing ? {
            unitPrice: pricing.rawUnitPrice || undefined,
            quantity: pricing.rawQuantity || undefined,
            unit: c.unit || undefined,
            extendedPrice: pricing.rawExtendedPrice || undefined,
            ceiling: pricing.rawCeiling || undefined,
            unitPriceParsed: pricing.unitPriceParsed ? {
                rawValue: pricing.unitPriceParsed.rawValue,
                numericValue: pricing.unitPriceParsed.numericValue || undefined,
                isNSP: pricing.unitPriceParsed.isNsp || undefined,
                isNTE: pricing.unitPriceParsed.isNte || undefined,
                nteCeiling: pricing.unitPriceParsed.nteCeiling || undefined,
                isTBD: pricing.unitPriceParsed.isTbd || undefined
            } : undefined,
            extendedPriceParsed: pricing.extendedPriceParsed ? {
                rawValue: pricing.extendedPriceParsed.rawValue,
                numericValue: pricing.extendedPriceParsed.numericValue || undefined,
                isNSP: pricing.extendedPriceParsed.isNsp || undefined,
                isNTE: pricing.extendedPriceParsed.isNte || undefined,
                nteCeiling: pricing.extendedPriceParsed.nteCeiling || undefined,
                isTBD: pricing.extendedPriceParsed.isTbd || undefined
            } : undefined
        } : undefined,
        exerciseInfo: c.exerciseInfo ? {
            farClause: c.exerciseInfo.farClause || undefined,
            exerciseFrequency: c.exerciseInfo.exerciseFrequency || undefined,
            popMonths: c.exerciseInfo.popMonths || undefined,
            popDescription: c.exerciseInfo.popDescription || undefined,
            popIsConditional: c.exerciseInfo.popIsConditional || undefined,
            allowsCrossPeriod: c.exerciseInfo.allowsCrossPeriod || undefined,
            popUpperBound: c.exerciseInfo.popUpperBound || undefined
        } : undefined,
        scopedPwsRefs: c.scopedPwsRefs?.length ? c.scopedPwsRefs.map((ref)=>({
                section: ref.section,
                title: ref.title || undefined,
                includesAll: ref.includesAll,
                onlyChildren: ref.onlyChildren,
                excludes: ref.excludes,
                expandedSections: ref.expandedSections,
                rawText: ref.rawText || undefined
            })) : undefined,
        sourceDocumentId: c.sourceDocumentId || undefined,
        sourceFilename: c.sourceFilename || undefined,
        isFromAmendment: c.isFromAmendment || undefined,
        amendmentNumber: c.amendmentNumber || undefined,
        amendmentSequence: c.amendmentSequence || undefined,
        logicalKey: c.logicalKey || undefined,
        isCurrentVersion: c.isCurrentVersion || undefined
    };
}
function mapLCAT(l) {
    return {
        code: l.lcatId,
        title: l.title,
        category: l.level || 'general',
        level: l.level || undefined,
        baseHourlyRate: l.hourlyRate || undefined,
        ceilingRate: l.ceilingRate || undefined,
        estimatedHours: l.estimatedHours || undefined,
        description: l.description || undefined,
        alternativeTitles: l.alternativeTitles,
        certifications: l.certifications,
        clearance: l.clearanceRequired || undefined,
        clinNumber: l.clinNumber || undefined,
        isKeyPersonnel: l.isKeyPersonnel || undefined,
        teamName: l.teamName || undefined,
        confidence: l.confidence || undefined,
        reasoning: l.reasoning || undefined,
        pwsReferences: l.pwsSections,
        attachmentReferences: l.attachmentReferences,
        extractedAt: l.extractedAt || undefined,
        status: l.status || undefined,
        notes: l.notes || undefined,
        tags: l.tags,
        education: l.educationDetail ? {
            level: l.educationDetail.level || undefined,
            field: l.educationDetail.field || undefined,
            substitution: l.educationDetail.substitution || undefined
        } : undefined,
        experience: l.experienceDetail ? {
            minimumYears: l.experienceDetail.minimumYears || undefined,
            type: l.experienceDetail.type || undefined,
            description: l.experienceDetail.description || undefined
        } : undefined,
        clinLinkages: l.clinLinkages?.length ? l.clinLinkages.map((link)=>({
                clinNumber: link.clinNumber,
                reasoning: link.reasoning || undefined,
                confidence: link.confidence || undefined,
                pwsReferences: link.pwsReferences,
                attachmentReferences: link.attachmentReferences
            })) : undefined,
        sourceDocumentId: l.sourceDocumentId || undefined,
        sourceFilename: l.sourceFilename || undefined,
        isFromAmendment: l.isFromAmendment || undefined,
        amendmentNumber: l.amendmentNumber || undefined,
        amendmentSequence: l.amendmentSequence || undefined,
        logicalKey: l.logicalKey || undefined,
        isCurrentVersion: l.isCurrentVersion || undefined
    };
}
function toBatchDocStatus(status) {
    switch(status){
        case 'ready':
            return 'completed';
        case 'error':
            return 'failed';
        case 'processing':
            return 'processing';
        default:
            return 'pending';
    }
}
function toNotAContractReason(raw) {
    return raw === 'solicitation' || raw === 'not_a_contract' || raw === 'unconfirmed' ? raw : undefined;
}
// ---------------------------------------------------------------------------
// API implementation
// ---------------------------------------------------------------------------
/** How long a batch's file list is reused while the batch has not moved. */ const BATCH_FILES_MAX_AGE_MS = 10000;
const batchFiles = new Map();
/**
 * The project's files for a batch status poll. The list changes only as the
 * batch moves, so it is read again only when the batch's progress changes or
 * once it is 10s old, not on every 2s poll (SAD-993).
 */ async function batchDocuments(projectId, batchId, progress) {
    const key = `${projectId}:${batchId}`;
    const held = batchFiles.get(key);
    if (held && held.progress === progress && Date.now() - held.at < BATCH_FILES_MAX_AGE_MS) {
        return held.documents;
    }
    try {
        const filesResp = await projectDataClient.listProjectFiles({
            projectId
        });
        const documents = (filesResp.files ?? []).map((f)=>({
                attachmentId: f.fileId,
                filename: f.filename,
                status: toBatchDocStatus(f.status),
                documentType: f.documentType
            }));
        batchFiles.set(key, {
            progress,
            at: Date.now(),
            documents
        });
        return documents;
    } catch  {
        // Non-critical — documents list is optional
        return held?.documents ?? [];
    }
}
export const proposalProjectApiConnect = {
    async getComplianceMatrix (projectId, filters) {
        const response = await projectDataClient.queryComplianceRequirements({
            projectId,
            status: filters?.status ?? [],
            category: filters?.category ? [].concat(filters.category) : [],
            pagination: {
                pageSize: 5000
            }
        });
        const stats = response.stats;
        const byStatus = stats?.byStatus ?? {};
        const total = stats?.totalRequirements ?? response.requirements?.length ?? 0;
        // zou keys by_status by the raw DB enum (ADDRESSED, PARTIAL, IN_PROGRESS,
        // NOT_STARTED, NOT_APPLICABLE, NON_COMPLIANT), so we look up the same case.
        const addressed = byStatus.ADDRESSED ?? 0;
        const partial = (byStatus.PARTIAL ?? 0) + (byStatus.IN_PROGRESS ?? 0);
        const notApplicable = byStatus.NOT_APPLICABLE ?? 0;
        const notAddressed = Math.max(total - addressed - partial - notApplicable, 0);
        const score = total > 0 ? (addressed + partial * 0.5) / total * 100 : 0;
        return {
            requirements: (response.requirements ?? []).map(mapRequirement),
            statistics: {
                total,
                addressed,
                partial,
                notAddressed,
                notApplicable,
                complianceScore: score
            },
            pagination: {
                page: 1,
                limit: 100,
                total: response.requirements?.length ?? 0,
                pages: 1
            },
            evaluationScheme: response.evaluationScheme ? mapEvaluationScheme(response.evaluationScheme) : undefined
        };
    },
    async getShredView (projectId, filters) {
        const response = await projectDataClient.queryComplianceRequirements({
            projectId,
            status: filters?.status ?? [],
            category: filters?.category ? [
                filters.category
            ] : [],
            pagination: {
                pageSize: 5000
            }
        });
        return {
            category: filters?.category ?? '',
            requirements: (response.requirements ?? []).map(mapRequirement),
            stats: {
                total: response.requirements?.length ?? 0,
                byStatus: {},
                byObligation: {},
                byCategory: {},
                mandatory: 0,
                evaluated: 0
            },
            pagination: {
                page: 1,
                limit: 100,
                total: response.requirements?.length ?? 0,
                pages: 1
            }
        };
    },
    async updateRequirementStatus (projectId, requirementId, request) {
        return projectDataClient.updateRequirementStatus({
            projectId,
            requirementId,
            status: request.status ?? '',
            notes: request.statusNote ?? request.notes ?? ''
        });
    },
    async getCLINs (projectId) {
        const response = await projectDataClient.queryCLINs({
            projectId,
            pagination: {
                pageSize: 1000
            }
        });
        let deliverables = [];
        try {
            const deliverableResp = await projectDataClient.queryDeliverables({
                projectId,
                pagination: {
                    pageSize: 5000
                }
            });
            deliverables = deliverableResp.deliverables ?? [];
        } catch  {
        // Non-fatal: CLINs should still load if deliverables query fails.
        }
        const deliverablesByClinNumber = new Map();
        for (const d of deliverables){
            const clinNumber = d.clinNumber;
            if (!clinNumber) continue;
            const mapped = mapStoredDeliverable(d);
            const existing = deliverablesByClinNumber.get(clinNumber);
            if (existing) {
                existing.push(mapped);
            } else {
                deliverablesByClinNumber.set(clinNumber, [
                    mapped
                ]);
            }
        }
        const clins = (response.clins ?? []).map((c)=>mapCLIN(c, deliverablesByClinNumber));
        return {
            clins,
            stats: {
                totalCLINs: clins.length,
                baseCLINs: 0,
                optionCLINs: 0,
                totalValue: 0,
                fundedValue: 0
            },
            ...mapReferenceContent(response.referenceContent)
        };
    },
    async updateCLIN (projectId, clinId, request) {
        // Patch semantics: pass through every field the caller set. Zou's
        // UpdateCLIN handler treats empty strings as "field omitted" (matching
        // the existing `notes` convention), so we can safely forward defaults
        // without nulling out columns the caller didn't touch.
        //
        // Pricing is flattened onto the wire as raw user-typed strings —
        // request.pricing.unitPrice / extendedPrice come straight from the
        // drawer's text input. Zou parses them into the float columns and
        // also persists the raw text into pricing_raw_*.
        return projectDataClient.updateCLIN({
            projectId,
            clinId,
            fundingStatus: request.fundingStatus ?? '',
            notes: request.notes ?? '',
            title: request.title ?? '',
            description: request.description ?? '',
            unitPrice: request.pricing?.unitPrice ?? '',
            extendedPrice: request.pricing?.extendedPrice ?? ''
        });
    },
    async getLCATs (projectId) {
        const response = await projectDataClient.queryLCATs({
            projectId,
            pagination: {
                pageSize: 1000
            }
        });
        const lcats = (response.laborCategories ?? []).map(mapLCAT);
        return {
            laborCategories: lcats,
            stats: {
                totalCategories: lcats.length,
                averageRate: 0,
                minRate: 0,
                maxRate: 0
            }
        };
    },
    async updateLCAT (projectId, lcatId, request) {
        // Patch semantics: pass through every field the caller set. The
        // server's UpdateLCAT handler treats zero-values as "not provided"
        // (matching the existing `notes` convention), so we can safely
        // forward defaults without nulling out columns the caller didn't
        // touch. `category` is intentionally not on the proto today — the
        // LCAT model has no dedicated category column and the frontend's
        // "category" display reads from `level`, so the field is dropped
        // here on purpose until that read-side bug is addressed.
        return projectDataClient.updateLCAT({
            projectId,
            lcatId,
            notes: request.notes ?? '',
            title: request.title ?? '',
            description: request.description ?? '',
            level: request.level ?? '',
            baseHourlyRate: request.baseHourlyRate ?? 0,
            estimatedHours: request.estimatedHours ?? 0
        });
    },
    async getMasterMatrix (projectId) {
        const response = await projectDataClient.getMasterMatrix({
            projectId
        });
        const reqs = response.requirements ?? [];
        const unchanged = reqs.filter((r)=>r.amendmentStatus === 'unchanged').length;
        const added = reqs.filter((r)=>r.amendmentStatus === 'added').length;
        const modified = reqs.filter((r)=>r.amendmentStatus === 'modified').length;
        const removed = reqs.filter((r)=>r.amendmentStatus === 'removed').length;
        return {
            requirements: reqs,
            amendments: response.amendments ?? [],
            summary: {
                total: reqs.length,
                unchanged,
                added,
                modified,
                removed
            }
        };
    },
    async getPageVisualization (projectId, documentId, pageNumber) {
        const response = await projectDataClient.getPageVisualization({
            projectId,
            documentId,
            pageNumber
        });
        return {
            imageUrl: response.imageDataUrl || '',
            pageNumber: response.pageNumber,
            totalPages: response.totalPages,
            dimensions: {
                width: response.pageWidth,
                height: response.pageHeight
            },
            blocks: (response.blocks ?? []).map((b)=>({
                    id: b.blockId,
                    type: b.type,
                    content: b.content,
                    boundingBox: b.boundingBox ? {
                        x: b.boundingBox.x,
                        y: b.boundingBox.y,
                        width: b.boundingBox.width,
                        height: b.boundingBox.height
                    } : {
                        x: 0,
                        y: 0,
                        width: 0,
                        height: 0
                    },
                    confidence: b.confidence
                }))
        };
    },
    async processBatch (projectId, attachmentIds, options) {
        // documentKind (SAD-246) declares the batch's contract shape up front —
        // e.g. a Contract re-upload through Docs -> Modifications — so reverie's
        // content classifier can be overridden rather than guessed from the
        // document alone. Omitted preserves today's classify-by-content behavior.
        let documentKind = ProjectDataTypes.ReprocessDocumentKind.UNSPECIFIED;
        if (options?.documentKind === 'base') {
            documentKind = ProjectDataTypes.ReprocessDocumentKind.BASE;
        } else if (options?.documentKind === 'modification') {
            documentKind = ProjectDataTypes.ReprocessDocumentKind.MODIFICATION;
        } else if (options?.documentKind === 'attachment') {
            documentKind = ProjectDataTypes.ReprocessDocumentKind.ATTACHMENT;
        } else if (options?.documentKind === 'sow') {
            documentKind = ProjectDataTypes.ReprocessDocumentKind.SOW;
        }
        const response = await projectDataClient.processBatch({
            projectId,
            attachmentIds,
            documentKind,
            awardIntakeOnly: options?.awardIntakeOnly ?? false,
            workstreamId: options?.workstreamId ?? ''
        });
        return {
            batchId: response.batchId,
            status: response.status,
            totalDocuments: response.totalDocuments
        };
    },
    // Proportional recovery for a program_docs_incomplete batch: re-links the
    // documents into the existing program's Docs tab without re-running extraction
    // (baltigo + classification + LLM). Returns a batch to poll exactly like
    // processBatch. Same (projectId, ids) => {batchId} shape so the retry-toast
    // builder can call either.
    async relinkProgramDocuments (projectId, attachmentIds) {
        const response = await projectDataClient.relinkProgramDocuments({
            projectId,
            attachmentIds
        });
        return {
            batchId: response.batchId,
            status: response.status,
            totalDocuments: attachmentIds.length
        };
    },
    async reprocessContractExtraction (projectId, reextractDocId, options) {
        const documentKind = options?.documentKind === 'modification' ? ProjectDataTypes.ReprocessDocumentKind.MODIFICATION : ProjectDataTypes.ReprocessDocumentKind.BASE;
        const response = await projectDataClient.reprocessContractExtraction({
            projectId,
            reextractDocId,
            documentKind,
            programId: options?.programId ?? ''
        });
        return {
            batchId: response.batchId,
            status: response.status,
            totalDocuments: 0
        };
    },
    async getBatchStatus (projectId, batchId) {
        const response = await projectDataClient.getBatchStatus({
            projectId,
            batchId
        });
        const total = response.totalDocuments ?? 0;
        const processed = response.processedDocuments ?? 0;
        const failed = response.failedDocuments ?? 0;
        // zou status lifecycle:
        //   batch.Status="processing"  → per-doc jobs running
        //   batch.Status="completed"   → per-doc done (analysis may still be running)
        //   batch.AnalysisStatus="completed" → everything done (analysis + sync)
        //
        // UI view selection:
        //   View 1 (per-doc progress):    status='processing'
        //   View 2 (cross-analysis):      status='completed' && !trackComplete
        //   View 3 (done):                status='completed' && trackComplete
        const perDocDone = response.status === 'completed';
        const analysisStatus = response.projectAnalysisStatus || '';
        const analysisProgress = response.projectAnalysisProgress ?? 0;
        const analysisComplete = analysisStatus === 'completed';
        const fullyDone = perDocDone && analysisComplete;
        const pct = total > 0 ? Math.round((processed + failed) / total * 100) : 0;
        const documents = await batchDocuments(projectId, batchId, `${response.status}|${processed}|${failed}|${analysisStatus}`);
        return {
            batchId: response.batchId,
            // status drives which view renders:
            // 'processing' → View 1, 'completed' → View 2 or 3 (based on trackComplete)
            // 'failed' must survive. Collapsing every non-completed source status to
            // 'processing' meant a failed batch never set isFailed, so useProposalProgress
            // polled forever and the reprocess control never rendered. The REST client
            // returned the raw status, so this only became reachable everywhere once
            // the REST path was removed.
            status: response.status === 'failed' ? 'failed' : perDocDone ? 'completed' : 'processing',
            totalDocuments: total,
            processedDocuments: processed,
            failedDocuments: failed,
            documents,
            error: null,
            programSyncFailed: response.programSyncFailed ?? false,
            noContractAward: response.noContractAward ?? false,
            programDocsIncomplete: response.programDocsIncomplete ?? false,
            notAContract: toNotAContractReason(response.notAContract),
            syncedProgramId: response.syncedProgramId ?? '',
            attachmentIds: response.attachmentIds ?? [],
            // Drives View 2 (cross-analysis UI) — read directly from zou's proto fields
            projectAnalysisStatus: perDocDone && !analysisComplete ? analysisStatus || 'processing' : analysisComplete ? 'completed' : undefined,
            projectAnalysisProgress: perDocDone && !analysisComplete ? {
                percentage: analysisProgress,
                currentStep: ''
            } : undefined,
            progress: {
                percentage: perDocDone ? 100 : pct,
                currentStep: fullyDone ? 'Done' : analysisStatus === 'not_required' ? 'Award document ready for extraction' : perDocDone ? 'Cross-document analysis in progress' : `Processing document ${processed + failed + 1} of ${total}`,
                documentsProcessed: processed,
                totalDocuments: total,
                // Per-doc phases (convert/classify/index) for View 1.
                // shred/analyze/track are project_analysis work — only true when fully done.
                // track=true makes trackComplete=true → transitions View 2 → View 3.
                phaseStatus: {
                    convert: processed > 0,
                    classify: processed > 0,
                    index: perDocDone,
                    shred: fullyDone,
                    analyze: fullyDone,
                    track: fullyDone
                }
            }
        };
    },
    async getDocuments (projectId, filters) {
        const response = await projectDataClient.getDocuments({
            projectId,
            documentType: filters?.documentType ?? '',
            processingStatus: filters?.processingStatus ?? ''
        });
        return {
            documents: response.documents ?? [],
            total: response.total ?? 0
        };
    },
    async getSolicitationInsights (projectId) {
        const response = await projectDataClient.getSolicitationInsights({
            projectId
        });
        // Parse the new pipeline JSON blob if present. The backend writes it as
        // an opaque string in the proto so we can evolve the schema without
        // proto regen on every change. Empty when the insights pipeline didn't
        // run for this project (set INSIGHTS_PIPELINE_ENABLED=true on baltigo).
        let pipeline;
        if (response.insightsPipelineJson) {
            try {
                pipeline = JSON.parse(response.insightsPipelineJson);
            } catch (err) {
                // Bad JSON shouldn't break the whole panel — log and continue with
                // the legacy fields.
                // eslint-disable-next-line no-console
                console.warn('Failed to parse insightsPipelineJson:', err);
            }
        }
        return {
            evaluationScheme: response.evaluationScheme ? mapEvaluationScheme(response.evaluationScheme) : undefined,
            pageLimits: (response.pageLimits ?? []).map((p)=>({
                    scope: p.scope,
                    limit: p.limit,
                    unit: p.unit,
                    requirementId: p.requirementId,
                    sourceText: p.sourceText
                })),
            keyPersonnel: (response.keyPersonnel ?? []).map((k)=>({
                    role: k.role,
                    qualifications: k.qualifications || undefined,
                    clearanceLevel: k.clearanceLevel || undefined,
                    resumeRequired: k.resumeRequired,
                    requirementIds: k.requirementIds
                })),
            deadlines: (response.deadlines ?? []).map((d)=>({
                    type: d.type,
                    date: d.date,
                    description: d.description,
                    requirementId: d.requirementId
                })),
            formatRules: (response.formatRules ?? []).map((f)=>({
                    rule: f.rule,
                    scope: f.scope,
                    requirementId: f.requirementId
                })),
            securityRequirements: (response.securityRequirements ?? []).map((s)=>({
                    clearanceLevel: s.clearanceLevel,
                    scope: s.scope,
                    requirementIds: s.requirementIds
                })),
            pipeline
        };
    },
    /**
   * Dev-only: re-sync extracted data from MinIO → zou DB without re-extracting.
   * Useful when iterating on sync logic or frontend mappers.
   */ async resyncExtractedData (projectId, companyId) {
        const response = await projectDataClient.resyncExtractedData({
            projectId,
            companyId
        });
        return {
            requirementsSynced: response.requirementsSynced,
            clinsSynced: response.clinsSynced,
            lcatsSynced: response.lcatsSynced,
            classificationsSynced: response.classificationsSynced,
            opportunitySynced: response.opportunitySynced
        };
    }
};
